import { useEffect, useState } from "react";
import { getCostByAgent, getCostByDay, getCostSummary } from "../api/ops.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface CostSummary {
  total_cost_usd: number;
  total_input_tokens: number;
  total_output_tokens: number;
  record_count: number;
}
interface AgentCost {
  agent_name: string;
  total_cost_usd: number;
  job_count: number;
}
interface DayCost {
  day: string;
  total_cost_usd: number;
}

export function OpsCostsPage() {
  const { token } = useAuth();
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [byAgent, setByAgent] = useState<AgentCost[]>([]);
  const [byDay, setByDay] = useState<DayCost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString();
    void Promise.all([
      getCostSummary(token, { since: thirtyDaysAgo }),
      getCostByAgent(token, thirtyDaysAgo),
      getCostByDay(token, { since: thirtyDaysAgo, limit: 14 }),
    ]).then(([s, a, d]: unknown[]) => {
      setSummary(s as CostSummary);
      setByAgent((a as { items: AgentCost[] }).items ?? []);
      setByDay((d as { items: DayCost[] }).items ?? []);
      setLoading(false);
    });
  }, []);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <h1 className="mb-6 text-2xl font-bold text-white">Cost Dashboard</h1>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <>
            {summary && (
              <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
                {[
                  {
                    label: "Total Cost (30d)",
                    value: `$${(summary.total_cost_usd ?? 0).toFixed(4)}`,
                  },
                  {
                    label: "Input Tokens",
                    value: (summary.total_input_tokens ?? 0).toLocaleString(),
                  },
                  {
                    label: "Output Tokens",
                    value: (summary.total_output_tokens ?? 0).toLocaleString(),
                  },
                  { label: "Total Jobs", value: summary.record_count },
                ].map((s) => (
                  <div key={s.label} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                    <p className="text-xs text-gray-400">{s.label}</p>
                    <p className="mt-1 text-xl font-bold text-white">{s.value}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <h2 className="mb-3 text-sm font-medium text-gray-300">Cost by Agent</h2>
                <div className="space-y-2">
                  {byAgent.length === 0 && <p className="text-sm text-gray-500">No data</p>}
                  {byAgent.map((a) => (
                    <div key={a.agent_name} className="flex items-center justify-between text-sm">
                      <span className="text-gray-300">{a.agent_name}</span>
                      <div className="text-right">
                        <span className="text-white">${(a.total_cost_usd ?? 0).toFixed(4)}</span>
                        <span className="ml-2 text-xs text-gray-500">{a.job_count} jobs</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <h2 className="mb-3 text-sm font-medium text-gray-300">
                  Daily Cost (Last 14 days)
                </h2>
                <div className="space-y-1">
                  {byDay.length === 0 && <p className="text-sm text-gray-500">No data</p>}
                  {byDay.map((d) => (
                    <div key={d.day} className="flex items-center justify-between text-xs">
                      <span className="text-gray-400">{d.day}</span>
                      <span className="text-white">${(d.total_cost_usd ?? 0).toFixed(4)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
